import {
  BtnGroup,
  BtnToolbar,
  Button,
  ButtonAction,
  Check,
  Datepicker,
  IconTitle,
  Number,
  PageTitle,
  Text
} from '@dec/shared';
import { yupResolver } from '@hookform/resolvers/yup';
import { t } from 'i18next';
import { CardChecklist, InputCursorText } from 'react-bootstrap-icons';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { notification } from '@/app/core/notifications/notification';

import { useCatalog } from '../../hooks/useCatalog';
import { type CatalogValueEditable, type ColDefinitionModel } from '../../models/Catalog';
import { type CatalogValue } from '../../models/Catalog';
import { catalogService } from '../../services/catalog.service';

interface CatalogValueForm {
  id: string;
  code: string;
  name: string;
  additional?: Record<string, number | Date | string | boolean>;
}

interface CatalogValueEditProps {
  catalogId: string;
  original?: CatalogValue;
  handleCancel: () => void;
  handleFinish: () => void;
}

export const CatalogValueEdit = ({
  catalogId,
  handleCancel,
  handleFinish,
  original
}: CatalogValueEditProps) => {
  // se configuran las validaciones del formulario, cuales son las reglas que debe cumplir
  const schema: yup.ObjectSchema<CatalogValueForm> = yup.object({
    id: yup.string().nullable().required(),
    code: yup.string().nullable().required(),
    name: yup.string().nullable().required(),
    additional: yup.object().default({})
  });

  const initialValues: CatalogValueForm | undefined = original
    ? {
        id: original.id!,
        code: original.code!,
        name: original.name!,
        additional: original.additionalFields
      }
    : undefined;

  // se construye el formulario con los valores recibiods y las reglas por aplicar
  // esto nos devuelve los métodos para su gestión (submit, reset, etc)
  const methods = useForm<CatalogValueForm>({
    defaultValues: initialValues,
    resolver: yupResolver(schema)
  });
  // vamos a utilizar el manejo de submit y el manejo de reset
  const { handleSubmit } = methods;

  const handleSave = async (values: CatalogValueForm) => {
    console.log({ submit: values });

    // se va a guardar los datos del formulario
    const content: CatalogValueEditable = {
      id: values.id,
      // el catalogId esta dado de la invocación a la pantalla
      catalogId: catalogId,
      code: values.code,
      name: values.name,
      additionalFields: values.additional ?? {},
      translations: []
    };

    if (original) {
      // si se trata de una edición
      await catalogService.updateValue(content, original!.id!);
    } else {
      await catalogService.createValue(content);
    }

    notification.success(t('common.messages.savedSucessfully'));

    handleFinish();
  };

  // definiciones adicionales para el catalogo
  const { data: catalog } = useCatalog(catalogId);
  const definitions = catalog?.definitions ?? [];

  console.log({ initial: initialValues });

  return (
    <>
      <PageTitle title={t('catalog.catalogValue')} icon={CardChecklist} />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(handleSave)} autoComplete="off">
          <div className="row">
            <div className="col-12 col-md-6 col-lg-4">
              <div className="card">
                <div className="card-body">
                  <IconTitle
                    title={t('catalog.value')}
                    icon={InputCursorText}
                    subtitle={t('catalog.valueDescription')}
                  />

                  <div className="row">
                    <div className="col-12">
                      <Text
                        label={t('catalog.fields.id')}
                        name="id"
                        readonly={original != undefined}
                      />
                    </div>
                  </div>
                  <div className="col-12">
                    <Text label={t('catalog.values.code')} name="code" />
                  </div>

                  <div className="col-12">
                    <Text label={t('catalog.values.name')} name="name" />
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-md-6 col-lg-8">
              {/* se muestran todas las definiciones asociadas con el item de catalogo */}
              <div>
                <IconTitle
                  title={t('catalog.fieldAdditional')}
                  icon={InputCursorText}
                  subtitle={t('catalog.fieldAdditionalDescription')}
                />

                <div className="row">
                  {definitions.length == 0 && (
                    <div className="col-12">
                      <div className="alert alert-info">{t('catalog.fieldAdditionalZero')} </div>
                    </div>
                  )}
                  {definitions.map((item: ColDefinitionModel) => (
                    // acá se tienen que presentar los contenidos de los componentes que
                    // fueron elegidos durante la definición del catalogo. allí se especifica
                    // si se trata de capturas de numero, fecha o texto
                    <div className="col-12" key={item.field}>
                      {/* aca se dibuja el componente representado por field */}
                      {item.type == 'text' && (
                        <Text label={item.label} name={`additional.${item.field}`} />
                      )}
                      {item.type == 'date' && (
                        <Datepicker label={item.label} name={`additional.${item.field}`} />
                      )}
                      {item.type == 'bool' && (
                        <Check label={item.label} name={`additional.${item.field}`} />
                      )}
                      {item.type == 'number' && (
                        <Number label={item.label} name={`additional.${item.field}`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <BtnToolbar>
            <BtnGroup>
              <Button action={ButtonAction.cancel} variant="secondary" onClick={handleCancel} />
            </BtnGroup>
            <BtnGroup>
              <Button action={ButtonAction.save} variant="primary" type={'submit'} />
            </BtnGroup>
          </BtnToolbar>
        </form>
      </FormProvider>
    </>
  );
};
